/**
 * Branch Profile Page - Individual store branch view
 * Shows branch details, hours, delivery info and branch-specific products
 */

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Store, Truck, Package, Star, Clock,
  Phone, Heart, ChevronRight, ShoppingCart, Calendar, DollarSign, Navigation
} from 'lucide-react';
import { toast } from 'sonner';
import { getProductImage } from '../../utils/constants';
import api from '../../services/api';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import VerifiedBadge from '../../components/marketplace/VerifiedBadge';

// Brand colors
const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322'
};

// Day names
const DAY_NAMES = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
  thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
};
const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Helper to get branch open status
const getBranchOpenStatus = (branch) => {
  if (!branch) return { is_open: false, message: 'Cargando...', reason: 'loading' };
  if (branch.is_manually_closed) {
    return { is_open: false, message: 'Cerrado temporalmente', reason: 'manual' };
  }
  if (!branch.is_active) {
    return { is_open: false, message: 'Sucursal inactiva', reason: 'inactive' };
  }
  
  const now = new Date();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const currentDay = dayNames[now.getDay()];
  const currentTime = now.toTimeString().slice(0, 5);
  
  const hours = branch.business_hours;
  if (!hours) {
    return { is_open: true, message: 'Abierto', reason: 'no_hours' };
  }
  
  const daySchedule = hours[currentDay];
  if (!daySchedule || !daySchedule.is_open) {
    return { is_open: false, message: 'Cerrado hoy', reason: 'day_closed' };
  }
  
  const openTime = daySchedule.open_time || '09:00';
  const closeTime = daySchedule.close_time || '18:00';
  
  if (currentTime < openTime) {
    return { is_open: false, message: `Abre a las ${openTime}`, reason: 'not_yet' };
  }
  if (currentTime >= closeTime) {
    return { is_open: false, message: 'Cerrado · Abre mañana', reason: 'closed' };
  }
  
  const [closeH, closeM] = closeTime.split(':').map(Number);
  const [currH, currM] = currentTime.split(':').map(Number);
  const minsUntilClose = (closeH * 60 + closeM) - (currH * 60 + currM);
  
  if (minsUntilClose <= 30) {
    return { is_open: true, message: `Cierra pronto · ${minsUntilClose} min`, reason: 'closing_soon' };
  }
  
  return { is_open: true, message: `Abierto · Cierra a las ${closeTime}`, reason: 'open' };
};

const BranchProfile = () => {
  const { storeId, branchId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState(null);
  const [branch, setBranch] = useState(null);
  const [products, setProducts] = useState([]);
  const [favorites, setFavorites] = useState(new Set());

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
    loadFavorites();
  }, [storeId, branchId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load store info
      const storeRes = await api.get(`/marketplace/stores/${storeId}`);
      // The store data is nested under 'store' key
      setStore(storeRes.data.store || storeRes.data);
      
      // Find the specific branch
      const branchData = storeRes.data.branches?.find(b => b.id === branchId);
      if (branchData) {
        setBranch(branchData);
      }
      
      // Load products for this branch
      const productsRes = await api.get(`/marketplace/search?business_id=${storeId}&branch_id=${branchId}&limit=50`);
      setProducts(productsRes.data.products || []);
      
    } catch (error) {
      console.error('Error loading branch:', error);
      toast.error('Error al cargar sucursal');
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    try {
      const res = await api.get(`/marketplace/favorites?customer_token=${token}`);
      setFavorites(new Set(res.data.map(f => f.product?.id || f.product_id)));
    } catch (e) {}
  };

  const toggleFavorite = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    const token = localStorage.getItem('customer_token');
    if (!token) {
      toast.info('Inicia sesión para guardar favoritos');
      return;
    }
    try {
      if (favorites.has(productId)) {
        const favRes = await api.get(`/marketplace/favorites?customer_token=${token}`);
        const fav = favRes.data.find(f => (f.product?.id || f.product_id) === productId);
        if (fav) {
          await api.delete(`/marketplace/favorites/${fav.favorite_id}?customer_token=${token}`);
          setFavorites(prev => { const n = new Set(prev); n.delete(productId); return n; });
          toast.success('Eliminado de favoritos');
        }
      } else {
        await api.post(`/marketplace/favorites?customer_token=${token}`, {
          product_id: productId,
          business_id: storeId,
          branch_id: branchId
        });
        setFavorites(prev => new Set([...prev, productId]));
        toast.success('Agregado a favoritos');
      }
    } catch (e) {
      toast.error('Error al actualizar favoritos');
    }
  };

  if (loading) {
    return (
      <MarketplaceLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin w-10 h-10 border-4 border-slate-200 border-t-orange-500 rounded-full"></div>
        </div>
      </MarketplaceLayout>
    );
  }

  if (!store || !branch) {
    return (
      <MarketplaceLayout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Sucursal no encontrada</h2>
          <button onClick={() => navigate(-1)} className="mt-4 text-orange-600 hover:underline">
            Volver
          </button>
        </div>
      </MarketplaceLayout>
    );
  }

  const openStatus = getBranchOpenStatus(branch);

  return (
    <MarketplaceLayout showFloatingCart={true}>
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => navigate(-1)} className="p-1 hover:bg-slate-100 rounded">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <Link to={`/tienda/${storeId}`} className="text-slate-500 hover:text-slate-700">
              {store.name}
            </Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-900 font-medium">{branch.name}</span>
          </div>
        </div>
      </div>

      {/* Branch Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Store Logo/Icon & Info */}
            <div className="flex items-start gap-4">
              <div className={`p-4 rounded-xl ${openStatus.is_open ? 'bg-green-50' : 'bg-red-50'}`}>
                <Store className={`w-10 h-10 ${openStatus.is_open ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <div>
                {/* Store Name (Main Title) with Verified Badge */}
                <Link 
                  to={`/tienda/${storeId}`} 
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <h1 className="text-2xl font-bold text-slate-900">{store?.name}</h1>
                  {store?.verified && <VerifiedBadge size="md" />}
                </Link>
                
                {/* Branch Name (Subtitle) */}
                <p className="text-lg text-slate-500 mt-0.5">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  {branch.name}
                </p>
                
                {/* Open/Closed Status */}
                <div className={`inline-flex items-center gap-2 mt-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                  openStatus.is_open 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${openStatus.is_open ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                  {openStatus.message}
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="flex-1 md:text-right space-y-2">
              <p className="text-slate-600 flex items-start gap-2 md:justify-end">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {branch.address}
              </p>
              {branch.phone && (
                <a href={`tel:${branch.phone}`} className="text-teal-600 flex items-center gap-2 md:justify-end hover:text-teal-700">
                  <Phone className="w-4 h-4" /> {branch.phone}
                </a>
              )}
              
              {/* Google Maps Button */}
              <button
                onClick={() => {
                  // Use coordinates if available, otherwise use address
                  const location = branch.location;
                  let mapsUrl;
                  if (location?.lat && location?.lng) {
                    mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`;
                  } else if (branch.address) {
                    mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(branch.address)}`;
                  }
                  if (mapsUrl) {
                    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium mt-2"
                data-testid="directions-btn"
              >
                <Navigation className="w-4 h-4" />
                Cómo llegar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Info Cards */}
          <div className="lg:col-span-1 space-y-4">
            {/* Business Hours Card */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-slate-400" />
                Horario de atención
              </h3>
              <div className="space-y-2">
                {branch.business_hours ? (
                  DAY_ORDER.map(day => {
                    const schedule = branch.business_hours[day];
                    const isToday = DAY_ORDER[(new Date().getDay() + 6) % 7] === day;
                    return (
                      <div 
                        key={day} 
                        className={`flex justify-between py-1.5 px-2 rounded ${isToday ? 'bg-orange-50' : ''}`}
                      >
                        <span className={`text-sm ${isToday ? 'font-medium text-orange-700' : 'text-slate-600'}`}>
                          {DAY_NAMES[day]}
                          {isToday && <span className="ml-1 text-xs">(hoy)</span>}
                        </span>
                        <span className={`text-sm ${schedule?.is_open ? 'text-slate-700' : 'text-red-500'}`}>
                          {schedule?.is_open 
                            ? `${schedule.open_time} - ${schedule.close_time}`
                            : 'Cerrado'
                          }
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-slate-500">Horario no configurado</p>
                )}
              </div>
            </div>

            {/* Delivery Info Card */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                <Truck className="w-5 h-5 text-slate-400" />
                Opciones de entrega
              </h3>
              <div className="space-y-3">
                {branch.pickup_enabled && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Store className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">Recolección en tienda</p>
                      <p className="text-xs text-blue-600">Recoge tu pedido en esta sucursal</p>
                    </div>
                  </div>
                )}
                {branch.delivery_enabled && (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <Truck className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-900">Entrega a domicilio</p>
                        <p className="text-xs text-green-600">
                          Radio de cobertura: {branch.delivery_radius_km} km
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-green-200">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">
                        {branch.delivery_fee > 0 
                          ? `Costo de envío: $${branch.delivery_fee.toFixed(2)}`
                          : 'Envío gratis'
                        }
                      </span>
                    </div>
                    {branch.delivery_zones && branch.delivery_zones.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-green-200">
                        <p className="text-xs text-green-700 font-medium mb-1">Zonas de entrega:</p>
                        {branch.delivery_zones.map((zone, i) => (
                          <p key={i} className="text-xs text-green-600">
                            {zone.min_km}-{zone.max_km} km: ${zone.fee.toFixed(2)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {branch.min_order_amount > 0 && (
                  <p className="text-sm text-slate-500 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Pedido mínimo: ${branch.min_order_amount.toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Products */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">
                Productos en esta sucursal
              </h2>
              <span className="text-sm text-slate-500">{products.length} productos</span>
            </div>

            {products.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No hay productos disponibles en esta sucursal</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {products.map(item => {
                  const product = item.product || item;
                  return (
                    <Link
                      key={product.id}
                      to={`/producto/${product.id}`}
                      className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-shadow group"
                    >
                      <div className="aspect-square bg-slate-50 relative overflow-hidden">
                        <img 
                          src={getProductImage(product.image_url)}
                          alt={product.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = getProductImage(); }}
                        />
                        <button 
                          onClick={(e) => toggleFavorite(e, product.id)}
                          className={`absolute top-2 right-2 p-2 bg-white rounded-full shadow-md transition-all hover:scale-110 ${
                            favorites.has(product.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${favorites.has(product.id) ? 'text-red-500 fill-red-500' : 'text-slate-400'}`} />
                        </button>
                      </div>
                      <div className="p-3">
                        <h3 className="font-medium text-slate-800 line-clamp-2 text-sm">{product.name}</h3>
                        <p className="text-lg font-bold mt-1" style={{ color: COLORS.navyBlue }}>
                          ${(product.price || 0).toFixed(2)}
                        </p>
                        {product.stock !== undefined && (
                          <p className="text-xs text-slate-400 mt-1">{product.stock} disponibles</p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </MarketplaceLayout>
  );
};

export default BranchProfile;
